// Tarjeta de producto de la grilla y de las filas que se deslizan (decisión D49), al estilo de `ventas`: todas del mismo
// ancho y del mismo alto, digan lo que digan. Foto en un cuadro blanco (o, sin foto, el ícono de lo que es sobre un gris
// suave), nombre siempre en dos renglones, presentación en uno, precio con su unidad y "Agregar" pegado abajo, que pasa
// a ser − cantidad + del mismo alto. El stock solo si importa: "Sin stock" o "Últimos N", sobre la foto. La foto entra
// con un fundido corto; mientras llega, su cuadro brilla como un esqueleto.
import { formatoGs } from '../compartido';
import { cx, Icono } from '@reparto/ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { bultosDe, fijarBultos } from '../datos/carrito';
import { cambiarCarrito, useCarrito } from '../datos/estado-carrito';
import { cantidadEnUnidad, nombreUnidad, presentacion } from '../datos/formato';
import type { Articulo, Frecuente } from '../datos/tipos';
import { ControlCantidad } from './Cantidad';
import { iconoDeProducto, useFundido } from './Miniatura';
import { topeDe } from './Producto';

/** Con esto o menos se avisa "Últimos N" (solo si la empresa muestra la cantidad). */
export const POCOS = 5;

/**
 * La foto cuadrada del producto en un cuadro blanco (también en modo oscuro: las fotos tienen fondo blanco) o su
 * marcador: el ícono de lo que es, en gris, sobre un gris suave. En las listas va la miniatura; en la ficha, la grande.
 */
export function FotoProducto({
  a,
  grande,
  chica,
  className,
}: {
  a: Pick<Articulo, 'nombre' | 'categoria' | 'foto_url' | 'foto_mini_url' | 'se_puede_pedir'>;
  grande?: boolean;
  /** En la fila del carrito: el ícono más chico. */
  chica?: boolean;
  className?: string;
}) {
  const foto = grande ? (a.foto_url ?? a.foto_mini_url) : (a.foto_mini_url ?? a.foto_url);
  const [fallo, setFallo] = useState(false);
  const conFoto = !!foto && !fallo;
  // Mientras llega, el cuadro es un esqueleto; la foto entra con un fundido corto.
  const fundido = useFundido(conFoto ? foto : null);
  return (
    <span
      aria-hidden="true"
      className={cx(
        'relative grid aspect-square w-full place-items-center overflow-hidden rounded-xl',
        !conFoto ? 'bg-stone-100 text-stone-500' : fundido.esperando ? 'esqueleto bg-stone-200' : 'bg-[#fff]',
        !a.se_puede_pedir && 'opacity-60 grayscale',
        className,
      )}
    >
      {conFoto ? (
        <img
          src={foto}
          width={grande ? 800 : 240}
          height={grande ? 800 : 240}
          loading="lazy"
          decoding="async"
          alt=""
          onError={() => setFallo(true)}
          {...fundido.props}
          className={cx('absolute inset-0 h-full w-full object-contain', chica ? 'p-0.5' : 'p-1', fundido.clase)}
        />
      ) : (
        <Icono
          nombre={iconoDeProducto(a.categoria, a.nombre)}
          tamano={grande ? 96 : chica ? 30 : 44}
          className="opacity-90"
        />
      )}
    </span>
  );
}

/** "Sin stock" o "Últimos N" sobre la foto; si hay de sobra (o no se informa), nada. */
export function MarcaStock({ a }: { a: Pick<Articulo, 'disponible' | 'stock_bultos'> }) {
  const sin = a.disponible === false || a.stock_bultos === 0;
  const pocos = !sin && a.stock_bultos !== null && a.stock_bultos <= POCOS;
  if (!sin && !pocos) return null;
  return (
    <span
      className={cx(
        'absolute top-1.5 left-1.5 rounded-full px-2 py-0.5 text-[13px] leading-5 font-semibold shadow-sm ring-1',
        sin ? 'bg-rose-50 text-rose-800 ring-rose-200' : 'bg-amber-50 text-amber-900 ring-amber-200',
      )}
    >
      {sin ? 'Sin stock' : `Últimos ${a.stock_bultos}`}
    </span>
  );
}

/** "✓ 2" sobre la foto, arriba a la derecha: cuántos hay en el carrito (el texto para el lector va aparte). */
export function InsigniaCarrito({ bultos }: { bultos: number }) {
  return (
    <span
      aria-hidden="true"
      className="absolute top-1.5 right-1.5 inline-flex h-7 min-w-7 items-center justify-center gap-0.5 rounded-full bg-primary-600 pr-2 pl-1.5 text-[14px] font-bold text-sobre-marca tabular-nums shadow-sm ring-2 ring-[#ffffff]"
    >
      <Icono nombre="check" tamano={18} />
      {bultos > 99 ? '99+' : bultos}
    </span>
  );
}

/**
 * El precio con el color de la marca y "por pack" (debajo, en la tarjeta: así no se corta). En la ficha, más grande y
 * al lado.
 */
export function PrecioGrande({
  precio,
  unidad,
  apilado,
  ficha,
}: {
  precio: number | null;
  unidad: Articulo['unidad'] | null;
  apilado?: boolean;
  ficha?: boolean;
}) {
  if (precio === null) return null;
  return (
    <p className="leading-tight">
      <span
        className={cx(
          'font-bold whitespace-nowrap text-primary-700 tabular-nums',
          apilado ? 'block text-lg leading-6' : ficha ? 'text-3xl md:text-4xl' : 'text-xl',
        )}
      >
        {formatoGs(precio)}
      </span>
      <span
        className={cx(
          'text-stone-600',
          ficha ? 'text-base' : apilado ? 'text-[13px] leading-5' : 'text-sm',
          apilado ? 'block' : 'ml-1.5',
        )}
      >
        por {nombreUnidad(unidad)}
      </span>
    </p>
  );
}

export function TarjetaProducto({
  a,
  permitirSinStock,
  frecuente,
}: {
  a: Articulo | Frecuente;
  permitirSinStock: boolean;
  /** En "Lo de siempre": entra con la cantidad de la última vez. */
  frecuente?: boolean;
}) {
  const bultos = useCarrito((s) => bultosDe(s.carrito, a.id));
  const sugeridos = frecuente && 'bultos_sugeridos' in a ? a.bultos_sugeridos : 1;
  const tope = topeDe(a, permitirSinStock);
  const ficha = `/producto/${encodeURIComponent(a.id)}`;
  // Solo si dice algo más que el "por pack" del precio; el renglón queda igual, para alinear las tarjetas.
  const detalle =
    a.unidad !== 'unidad' && (a.unidades_por_bulto ?? 0) > 1 ? presentacion(a.unidad, a.unidades_por_bulto) : '';
  // Ya está en el carrito: la tarjeta se marca (anillo y tinte de la marca, y la insignia sobre la foto), así se ve de un
  // vistazo qué se agregó. El estado también se dice con texto, no solo con el color.
  const enCarrito = bultos > 0;
  return (
    // relative: el "En el carrito" para el lector de pantalla (sr-only, absoluto) queda dentro de la tarjeta. Sin esto
    // se escapaba de la fila deslizable y estiraba la página al ancho de la fila (la barra de abajo quedaba corrida).
    <article
      className={cx(
        'relative flex h-full w-full flex-col rounded-2xl p-2 shadow-sm transition-colors',
        enCarrito ? 'bg-primary-50 ring-2 ring-primary-600 oscuro:ring-primary-400' : 'bg-white ring-1 ring-stone-200',
      )}
    >
      <Link
        to={ficha}
        state={{ articulo: a }}
        className="group flex flex-col rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      >
        <div className="relative">
          <FotoProducto a={a} />
          <MarcaStock a={a} />
          {enCarrito ? <InsigniaCarrito bultos={bultos} /> : null}
        </div>
        <div className="px-1 pt-2">
          <h3 className="line-clamp-2 h-10 text-[15px] leading-5 font-semibold text-stone-900 group-hover:underline">
            {a.nombre}
          </h3>
          <p className="h-5 truncate text-[13px] leading-5 text-stone-600">{detalle}</p>
          {enCarrito ? <span className="sr-only">En el carrito: {cantidadEnUnidad(bultos, a.unidad)}</span> : null}
        </div>
      </Link>
      <div className="px-1 pt-1">
        {a.precio_bulto !== null ? (
          <PrecioGrande precio={a.precio_bulto} unidad={a.unidad} apilado />
        ) : (
          // Sin precio a la vista (la empresa no lo muestra): el lugar queda, para alinear las tarjetas.
          <div className="h-11" />
        )}
        {frecuente && 'bultos_sugeridos' in a ? (
          <p className="truncate text-[13px] leading-5 text-stone-700">
            La última vez: <b className="font-semibold">{cantidadEnUnidad(sugeridos, a.unidad)}</b>
          </p>
        ) : null}
      </div>
      <div className="mt-auto pt-2">
        <ControlCantidad
          tarjeta
          valor={bultos}
          onCambiar={(n) => cambiarCarrito((c) => fijarBultos(c, a, n))}
          max={tope}
          nombre={a.nombre}
          deshabilitado={!a.se_puede_pedir && bultos === 0}
          textoAgregar={!a.se_puede_pedir ? 'Sin stock' : frecuente ? `Agregar ${sugeridos}` : 'Agregar'}
          alAgregar={sugeridos}
        />
      </div>
    </article>
  );
}

/** Grilla de tarjetas: 2 columnas en el celular, hasta 5 en computadora. */
export const CLASES_GRILLA = 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

/**
 * Cada tarjeta dentro de una fila que se desliza: todas del mismo ancho. En el celular se ven unas 2,3 (así se nota
 * que hay más al costado); desde tablet, 13rem fijos.
 */
export const CLASES_EN_CARRUSEL =
  'flex w-[clamp(8.75rem,calc((100vw_-_2.5rem)/2.3),13rem)] shrink-0 snap-start *:w-full';

/**
 * Una fila que se desliza con su encabezado: el título a la izquierda y, a la derecha, la acción ("Ver todos") y,
 * desde tablet, las flechas.
 */
export function Carrusel({
  nombre,
  titulo,
  accion,
  children,
}: {
  nombre: string;
  titulo?: ReactNode;
  accion?: ReactNode;
  children: ReactNode;
}) {
  const lista = useRef<HTMLUListElement>(null);
  const [bordes, setBordes] = useState({ inicio: true, fin: false });
  const medir = () => {
    const el = lista.current;
    if (el) setBordes({ inicio: el.scrollLeft < 2, fin: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 });
  };
  useEffect(() => {
    const el = lista.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(medir);
    o.observe(el);
    return () => o.disconnect();
  }, []);
  const mover = (dir: number) => {
    const el = lista.current;
    if (!el) return;
    const suave = typeof matchMedia === 'function' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: suave ? 'smooth' : 'auto' });
  };
  const boton =
    'grid h-10 w-10 place-items-center rounded-full border border-stone-300 bg-white text-stone-800 hover:bg-stone-100 focus-visible:outline-2 focus-visible:outline-primary-600 disabled:opacity-40';
  return (
    <div>
      <div className="mb-3 flex min-h-11 items-center justify-between gap-3">
        <div className="min-w-0">{titulo}</div>
        <div className="flex shrink-0 items-center gap-2">
          {accion}
          <button
            type="button"
            aria-label={`Productos anteriores de ${nombre}`}
            disabled={bordes.inicio}
            onClick={() => mover(-1)}
            className={cx(boton, 'hidden md:grid')}
          >
            <Icono nombre="chevron_left" />
          </button>
          <button
            type="button"
            aria-label={`Más productos de ${nombre}`}
            disabled={bordes.fin}
            onClick={() => mover(1)}
            className={cx(boton, 'hidden md:grid')}
          >
            <Icono nombre="chevron_right" />
          </button>
        </div>
      </div>
      <ul
        ref={lista}
        onScroll={medir}
        aria-label={`Productos de ${nombre}`}
        // pt-1 y md:px-1: el borde de la tarjeta marcada (ring, 2 px por fuera) no queda cortado por el overflow de la
        // fila, ni arriba ni a los costados en computadora (md:-mx-1 mantiene las tarjetas alineadas con el título).
        className="sin-scroll relative -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto overscroll-x-contain px-4 pt-1 pb-2 md:-mx-1 md:scroll-px-1 md:px-1"
      >
        {children}
      </ul>
    </div>
  );
}

/** Título de una sección del inicio: ícono, nombre y debajo, en chico, cuántos productos (o lo que corresponda). */
export function TituloFila({
  id,
  icono,
  children,
  detalle,
}: {
  id: string;
  icono?: string;
  children: ReactNode;
  detalle?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {icono ? <Icono nombre={icono} tamano={26} className="shrink-0 text-primary-700" /> : null}
      <div className="min-w-0">
        <h2 id={id} className="text-xl leading-6 font-bold text-balance text-stone-900">
          {children}
        </h2>
        {detalle ? <p className="mt-0.5 text-sm leading-5 text-stone-600">{detalle}</p> : null}
      </div>
    </div>
  );
}

/** "Ver todos →" de cada fila. */
export function VerTodos({ a, nombre, etiqueta }: { a: string; nombre?: string; etiqueta?: string }) {
  return (
    <Link
      to={a}
      aria-label={etiqueta ?? `Ver todos los productos de ${nombre}`}
      className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-full px-1 text-[15px] font-semibold text-primary-700 hover:underline focus-visible:outline-2 focus-visible:outline-primary-600"
    >
      Ver todos
      <Icono nombre="arrow_forward" tamano={20} />
    </Link>
  );
}
